import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { alpha, styled } from '@mui/material/styles';
import { Avatar, Box, Button, Drawer, Link, Typography } from '@mui/material';
import account from 'src/_mock/account';
import useResponsive from 'src/hooks/useResponsive';
import Scrollbar from 'src/components/scrollbar';
import NavSection from 'src/components/nav-section';
import navConfig from './config';
import Iconify from 'src/components/iconify';
import { env } from 'src/config/env';
import { TimezoneToggle } from 'src/components/Timezone/TimezoneToggle';

const NAV_WIDTH = 280;

const StyledAccount = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(2, 2.5),
  borderRadius: Number(theme.shape.borderRadius) * 1.5,
  backgroundColor: alpha(theme.palette.grey[500], 0.12),
}));

interface NavProps {
  openNav: boolean;
  onCloseNav: () => void;
}

export default function Nav({ openNav, onCloseNav }: NavProps) {
  const { pathname } = useLocation();

  const isDesktop = useResponsive('up', 'lg', 'xl');

  useEffect(() => {
    if (openNav) {
      onCloseNav();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const renderContent = (
    <Scrollbar
      sx={{
        height: 1,
        '& .simplebar-content': {
          height: 1,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Button
        sx={{ justifyContent: 'left', margin: '10px' }}
        variant='text'
        startIcon={<Iconify icon='eva:arrow-ios-back-outline' />}
        onClick={() => (window.location.href = env.LOW6_CLIENT_ADMIN_URL)}
      >
        Admin Panel
      </Button>

      <Box sx={{ mx: 'auto', my: 2 }}>
        <TimezoneToggle />
      </Box>

      <Box sx={{ mb: 5, mx: 2.5 }}>
        <Link underline='none'>
          <StyledAccount>
            <Avatar src={account.photoURL} alt='photoURL' />

            <Box sx={{ ml: 2 }}>
              <Typography variant='subtitle2' sx={{ color: 'text.primary' }}>
                {account.displayName}
              </Typography>

              <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                {account.role}
              </Typography>
            </Box>
          </StyledAccount>
        </Link>
      </Box>

      <NavSection data={navConfig} />

      <Box sx={{ flexGrow: 1 }} />
    </Scrollbar>
  );

  return (
    <Box
      component='nav'
      sx={{
        flexShrink: { lg: 0 },
        width: { lg: NAV_WIDTH },
      }}
    >
      {isDesktop ? (
        <Drawer
          open
          variant='permanent'
          PaperProps={{
            sx: {
              width: NAV_WIDTH,
              bgcolor: 'background.default',
              borderRightStyle: 'dashed',
            },
          }}
        >
          {renderContent}
        </Drawer>
      ) : (
        <Drawer
          open={openNav}
          onClose={onCloseNav}
          ModalProps={{
            keepMounted: true,
          }}
          PaperProps={{
            sx: { width: NAV_WIDTH },
          }}
        >
          {renderContent}
        </Drawer>
      )}
    </Box>
  );
}
